import { X } from "lucide-react";
import {
  Anchor,
  Grid,
  Input,
  InputWrapper,
  Space,
  Text,
  createStyles,
} from "@mantine/core";

import React, { useEffect, useState } from "react";
import { IBlockedDateEmployees } from "../../@types/blockedDates";

import useGetEmployeesFromListApiService from "../../services/api/GetEmployeesFromListApiService";
import { queryClient } from "../../../../App";
import ProgressIndicator from "../../../../themes/components/ProgressIndicator";

const useStyles = createStyles(() => ({
  gridContainer: {
    width: "100%",
    maxWidth: "100%",
    maxHeight: "10rem",
    overflowY: "auto",
    overflowX: "clip",
  },
  gridColumn: {
    display: "flex",
  },
  employeeBadge: {
    position: "relative",
    display: "flex",
    padding: "5px 7px",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: "8px",
    border: "1px solid #ED7787",
    background: "rgba(237, 119, 135, 0.10)",
    width: "100%",
    minHeight: "54px",
    "&:hover": {
      background: "#ED7787",
    },
  },
  employeeBadgeText: {
    position: "relative",
    alignSelf: "center",
    justifySelf: "center",
    textAlign: "center",
    fontFamily: "Inter",
    fontStyle: "normal",
    fontWeight: 700,
    fontSize: "14px",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    maxWidth: "100%",
  },
  employeeBadgeXIcon: {
    alignSelf: "flex-start",
    justifySelf: "flex-end",
    fontFamily: "Inter",
    fontStyle: "normal",
    fontWeight: 700,
    fontSize: "10px",
    maxWidth: "100%",
  },
  employeeNotFoundBadge: {
    position: "relative",
    display: "flex",
    padding: "5px 7px",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: "8px",
    border: "1px solid #ED7787",
    minHeight: "54px",
    width: "100%",
  },
}));

function removeDuplicates(inputString: string): string {
  // Convert the string to an array and then to a Set to remove duplicates
  const uniqueString = Array.from(new Set(inputString.split(",")));

  return uniqueString.join(",");
}

const removeEmployeeOccuranceFromIdentifiers = (
  identifiers: string,
  employeeIdentifier: string,
  separator: string | null = null
): string => {
  const separatorToUse = separator || ",";
  const values = identifiers.split(separatorToUse);
  /* eslint-disable-next-line no-plusplus */
  for (let i = 0; i < values.length; i++) {
    values[i] = values[i].trim();
    if (values[i] === employeeIdentifier) {
      values.splice(i, 1);
      return values.join(separatorToUse);
    }
  }
  if (Array.isArray(values)) {
    return values.join(separatorToUse);
  }
  return values;
};

const AddEmployeeToDeletedList = (
  employee: IBlockedDateEmployees,
  employeesCreate: IBlockedDateEmployees[],
  setEmployeesCreate: (val: IBlockedDateEmployees[]) => void,
  employeesDelete: IBlockedDateEmployees[],
  setEmployeesDelete: (val: IBlockedDateEmployees[]) => void,
  employeeIdentifiers: string,
  setEmployeeIdentifiers: (val: string) => void
): void => {
  const employeesCreateFiltered = employeesCreate.filter(
    (createEmployee) => createEmployee.employee_id !== employee.employee_id
  );
  let newIdentifiers = employeeIdentifiers;
  if (employee.employee_number) {
    newIdentifiers = removeEmployeeOccuranceFromIdentifiers(
      newIdentifiers,
      employee.employee_number
    );
  }
  if (employee.employee_id_number) {
    newIdentifiers = removeEmployeeOccuranceFromIdentifiers(
      newIdentifiers,
      employee.employee_id_number
    );
  }
  setEmployeeIdentifiers(newIdentifiers);
  setEmployeesCreate(employeesCreateFiltered);
  setEmployeesDelete([...employeesDelete, employee]);
};

interface IEmployeeBadgeProps {
  employees: IBlockedDateEmployees[];
  employeeLimit: number;
  setEmployeesCreate: (val: IBlockedDateEmployees[]) => void;
  employeesDelete: IBlockedDateEmployees[];
  setEmployeesDelete: (val: IBlockedDateEmployees[]) => void;
  employeeIdentifiers: string;
  setEmployeeIdentifiers: (val: string) => void;
}
const EmployeeBadge: React.FC<IEmployeeBadgeProps> = ({
  employees,
  employeeLimit,
  setEmployeesCreate,
  employeesDelete,
  setEmployeesDelete,
  employeeIdentifiers,
  setEmployeeIdentifiers,
}: IEmployeeBadgeProps): any => {
  const { classes } = useStyles();
  const gridEmployees = [];
  /* eslint-disable-next-line no-plusplus */
  for (let index = 0; index < employeeLimit; index++) {
    const employee = employees[index];
    gridEmployees.push(
      <Grid.Col
        key={`employee-badge-grid-${employee.employee_id}`}
        className={classes.gridColumn}
        span={6}
      >
        <button
          className={classes.employeeBadge}
          onClick={() => {
            AddEmployeeToDeletedList(
              employee,
              employees,
              setEmployeesCreate,
              employeesDelete,
              setEmployeesDelete,
              employeeIdentifiers,
              setEmployeeIdentifiers
            );
          }}
        >
          <div />
          <Text className={classes.employeeBadgeText}>
            {employee.employee_name}
          </Text>
          <Text className={classes.employeeBadgeXIcon}>
            <X size={16} />
          </Text>
        </button>
      </Grid.Col>
    );
  }

  return gridEmployees;
};

interface IEmployeeNotFoundBadge {
  employeesNotFound: string[];
  employeeNotFoundLimit: number;
}

const EmployeeNotFoundBadge: React.FC<IEmployeeNotFoundBadge> = ({
  employeesNotFound,
  employeeNotFoundLimit,
}: IEmployeeNotFoundBadge): any => {
  const { classes } = useStyles();
  const gridEmployees = [];

  /* eslint-disable-next-line no-plusplus */
  for (let index = 0; index < employeeNotFoundLimit; index++) {
    const employeeNotFound = employeesNotFound[index];
    gridEmployees.push(
      <Grid.Col key={`employee-badge-grid-${employeeNotFound}`} span={6}>
        <button className={classes.employeeNotFoundBadge}>
          <div />
          <Text className={classes.employeeBadgeText}>{employeeNotFound}</Text>
          <div />
        </button>
      </Grid.Col>
    );
  }

  return gridEmployees;
};

const validLengthEmployeeIdentifiers = (
  employeeIdentifiers: string
): boolean => {
  const employeeIdentifiersSplit = employeeIdentifiers.split(",");
  if (employeeIdentifiersSplit.length < 200) {
    return true;
  }
  return false;
};

type EmployeesBlockedProps = {
  isEmployeeBlockedDate: boolean;
  employeesCreate: IBlockedDateEmployees[];
  employeesDelete: IBlockedDateEmployees[];
  setEmployeesCreate: (val: IBlockedDateEmployees[]) => void;
  setEmployeesDelete: (val: IBlockedDateEmployees[]) => void;
};

const EmployeesBlocked: React.FC<EmployeesBlockedProps> = ({
  isEmployeeBlockedDate,
  employeesCreate,
  employeesDelete,
  setEmployeesCreate,
  setEmployeesDelete,
}) => {
  const { classes } = useStyles();
  const [employeeIdentifiers, setEmployeeIdentifiers] = useState("");
  const [employeesNotFound, setEmployeesNotFound] = useState<string[]>([]);
  const [fetchEmployeeData, setShouldFetchEmployeeData] =
    useState<boolean>(false);

  const [initialIdentifiers, setInitialIdentifiers] = useState("");

  const { data, isLoading, refetch } = useGetEmployeesFromListApiService(
    { employee_identifiers: employeeIdentifiers },
    { enabled: false }
  );
  const [employeeLimit, setEmployeeLimit] = useState<number>(1);
  const [employeeNotFoundLimit, setEmployeeNotFoundLimit] = useState<number>(1);
  const [isValidEmployeeIdentifiers, setIsValidEmployeeIdentifiers] =
    useState<boolean>(true);

  /* eslint-disable-next-line arrow-body-style */
  useEffect(() => {
    setInitialIdentifiers(
      employeesCreate.map((employee) => employee.employee_id_number).join(",")
    );
    setEmployeeIdentifiers(
      employeesCreate.map((employee) => employee.employee_id_number).join(",")
    );
    return () => {
      // lint for arrow-body-style disabled for clarity
      setInitialIdentifiers("");
      setEmployeeIdentifiers("");
      setShouldFetchEmployeeData(false);
      queryClient.resetQueries({
        queryKey: ["GetEmployeesFromListApiService"],
        exact: true,
      });
      setEmployeesNotFound([]);
      setEmployeesCreate([]);
      setEmployeesDelete([]);
    };
  }, []);

  useEffect(() => {
    if (employeesCreate.length > 5) {
      setEmployeeLimit(5);
    } else {
      setEmployeeLimit(employeesCreate.length);
    }
  }, [employeesCreate]);

  useEffect(() => {
    if (employeesNotFound.length > 5) {
      setEmployeeNotFoundLimit(5);
    } else {
      setEmployeeNotFoundLimit(employeesNotFound.length);
    }
  }, [employeesNotFound]);

  useEffect(() => {
    if (
      fetchEmployeeData &&
      employeeIdentifiers &&
      employeeIdentifiers.length > 0 &&
      validLengthEmployeeIdentifiers(employeeIdentifiers)
    ) {
      refetch();
    } else {
      setShouldFetchEmployeeData(false);
    }
  }, [fetchEmployeeData, employeeIdentifiers]);

  useEffect(() => {
    if (validLengthEmployeeIdentifiers(employeeIdentifiers)) {
      setIsValidEmployeeIdentifiers(true);
    } else {
      setIsValidEmployeeIdentifiers(false);
      setEmployeesNotFound([]);
      setEmployeesCreate([]);
      setEmployeesDelete([]);
    }
  }, [fetchEmployeeData]);

  useEffect(() => {
    if (data && fetchEmployeeData) {
      if (data.employees.length > 5) {
        setEmployeeLimit(5);
      } else {
        setEmployeeLimit(data.employees.length);
      }
      if (data.employees_not_found.length > 5) {
        setEmployeeNotFoundLimit(5);
      } else {
        setEmployeeLimit(data.employees_not_found.length);
      }
      setEmployeesCreate(data.employees);
      setEmployeesDelete([]);
      setEmployeesNotFound(data.employees_not_found);
      setShouldFetchEmployeeData(false);
      queryClient.resetQueries({
        queryKey: ["GetEmployeesFromListApiService"],
        exact: true,
      });
    }
  }, [data]);

  return isEmployeeBlockedDate ? (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "column",
      }}
    >
      <Text
        style={{
          fontSize: "14px",
          fontWeight: 600,
          lineHeight: "16px",
        }}
      >
        Enter Employee ID/Passport number (comma separated: example: 1234, 7596)
      </Text>
      <Text
        style={{
          color: "#737373",
          fontSize: "0.625rem",
          fontStyle: "normal",
          fontWeight: 500,
          lineHeight: "1.25rem",
        }}
      >
        Enter a comma-separated list of Employee{`'`}s ID or Passport Numbers
        below
      </Text>
      <InputWrapper id="input-limit" required style={{ width: "100%" }}>
        <Input
          id="input-limit"
          type="text"
          onChange={(e) => setEmployeeIdentifiers(e.target.value)}
          invalid={!isValidEmployeeIdentifiers}
          onBlur={(e) => {
            if (initialIdentifiers) {
              if (!e.target.value) {
                setEmployeeIdentifiers(
                  removeDuplicates(`${initialIdentifiers}`)
                );
              } else {
                setEmployeeIdentifiers(
                  removeDuplicates(`${initialIdentifiers},${e.target.value}`)
                );
              }
            } else {
              if (!e.target.value) {
                setEmployeesCreate([]);
                setEmployeesDelete([]);
                setEmployeesNotFound([]);
              }
              setEmployeeIdentifiers(removeDuplicates(e.target.value));
            }
            setShouldFetchEmployeeData(true);
          }}
          value={employeeIdentifiers}
        />
      </InputWrapper>
      <Space h="xl" />
      {isLoading ? (
        <ProgressIndicator />
      ) : (
        employeesCreate &&
        employeesCreate.length > 0 &&
        employeeLimit <= employeesCreate.length && (
          <>
            <Text
              style={{
                fontSize: "0.875rem",
                fontWeight: 600,
                lineHeight: "1.25rem",
                marginBottom: "0.5rem",
              }}
            >
              Employees Found ({employeesCreate.length})
            </Text>
            <div className={classes.gridContainer}>
              <Grid>
                <EmployeeBadge
                  employees={employeesCreate}
                  employeeLimit={employeeLimit}
                  setEmployeesCreate={setEmployeesCreate}
                  employeesDelete={employeesDelete}
                  setEmployeesDelete={setEmployeesDelete}
                  employeeIdentifiers={employeeIdentifiers}
                  setEmployeeIdentifiers={setEmployeeIdentifiers}
                />

                {employeeLimit !== employeesCreate.length && (
                  <Grid.Col span={6}>
                    <Anchor
                      component="button"
                      style={{ color: "var(--Neutral-black, #1A1A1A)" }}
                      onClick={() => {
                        setEmployeeLimit(employeesCreate.length);
                      }}
                    >
                      Load More...({employeesCreate.length - employeeLimit})
                    </Anchor>
                  </Grid.Col>
                )}
              </Grid>
            </div>
            <Space h="xl" />
          </>
        )
      )}
      {employeesNotFound && employeesNotFound.length > 0 && (
        <>
          <Text
            style={{
              fontSize: "0.875rem",
              fontWeight: 600,
              lineHeight: "1.25rem",
            }}
          >
            Employees Not Found ({employeesNotFound.length})
          </Text>
          <Text
            style={{
              color: "#737373",
              fontSize: "0.625rem",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "1.25rem",
            }}
          >
            No employees with the provided employee/ID number can be found. They
            will be excluded from the blocked date.
          </Text>
          <div className={classes.gridContainer}>
            <Grid>
              {employeesNotFound &&
                employeeNotFoundLimit <= employeesNotFound.length && (
                  <EmployeeNotFoundBadge
                    employeesNotFound={employeesNotFound}
                    employeeNotFoundLimit={employeeNotFoundLimit}
                  />
                )}
              {employeeNotFoundLimit !== employeesNotFound.length && (
                <Grid.Col span={6}>
                  <Anchor
                    component="button"
                    style={{ color: "var(--Neutral-black, #1A1A1A)" }}
                    onClick={() => {
                      setEmployeeNotFoundLimit(employeesNotFound.length);
                    }}
                  >
                    Load More...(
                    {employeesNotFound.length - employeeNotFoundLimit})
                  </Anchor>
                </Grid.Col>
              )}
            </Grid>
          </div>
        </>
      )}
    </div>
  ) : (
    <div />
  );
};

export default EmployeesBlocked;
